<template>
  <div
    :class="['surface-post-attachment', { 'post-attachment-on-top': showTallAttachmentPickerBox }]"
    :data-view-mode="isBeingEdited ? 'edit' : 'show'"
  >
    <div
      key="picker-notice"
      v-if="showTallAttachmentPickerBox && !fileUploadingToPickerBox"
      class="pick-content-text"
      v-t
    >
      Drag a file or choose from below
    </div>
    <div
      v-if="showMenu && showDeprecatedMenu"
      class="pick-attachment"
      @click.stop="openDeprecatedPicker"
      v-t:title="'Add attachment'"
    />

    <!-- Regular content picker -->
    <div v-if="showRegularAttachmentPickerBar" class="pick-content pick-content-regular">
      <upload-file
        v-if="fileUploadingToPickerBox && isBeingEdited"
        :file="fileUploadingToPickerBox"
        :max-file-size="uploadLimit"
        @error="onUploadError"
        @cancel="onUploadCancel"
        @done="onUploadDone"
        @reject="onUploadReject"
      />
      <template v-else>
        <button
          v-if="contentSourceCount > 1"
          class="icon-button"
          data-pepin-id="wish-upload"
          @click="getContentIfSupported(Source.FileBrowser)"
          v-t:title="'Upload'"
        >
          <label for="content-hidden-file-input"><base-icon name="upload" /></label>
        </button>
        <button
          v-if="contentSourceCount > 2"
          class="icon-button"
          data-pepin-id="wish-link"
          @click="getContent(Source.LinkInput)"
          v-t:title="'Link'"
        >
          <base-icon name="link" />
        </button>
        <button
          v-if="contentSourceCount > 3"
          class="icon-button"
          data-pepin-id="wish-search"
          @click="openSearchMenu"
          v-t:title="'Google'"
        >
          <base-icon name="search_web" />
        </button>
        <button
          v-if="contentSourceCount > 4"
          class="icon-button"
          data-pepin-id="wish-snap"
          @click="getContentIfSupported(Source.PhotoBooth)"
          v-t:title="'Snap'"
        >
          <label for="content-hidden-image-input"><base-icon name="camera" /></label>
        </button>
        <button
          v-if="contentSourceCount > 1"
          class="icon-button"
          data-pepin-id="wish-more"
          @click="chooseContentSource"
          v-t:title="'More'"
        >
          <base-icon name="more_horiz" />
        </button>
        <button
          v-if="contentSourceCount <= 1"
          class="icon-button"
          @click="chooseContentSource"
          v-t:title="'Add attachment'"
        >
          <base-icon name="attachment" />
        </button>
      </template>
    </div>

    <!-- Map and Timeline content picker -->
    <div v-else-if="showTallAttachmentPickerBox" class="pick-content pick-content-map">
      <upload-file
        v-if="fileUploadingToPickerBox && isBeingEdited"
        :file="fileUploadingToPickerBox"
        :max-file-size="uploadLimit"
        @error="onUploadError"
        @cancel="onUploadCancel"
        @done="onUploadDone"
        @reject="onUploadReject"
      />
      <template v-else>
        <button
          v-if="contentSourceCount > 1"
          class="icon-button"
          @click="getContentIfSupported(Source.FileBrowser)"
          v-t:title="'Upload'"
        >
          <label for="content-hidden-file-input"><base-icon name="upload" /></label>
        </button>
        <button
          v-if="contentSourceCount > 2"
          class="icon-button"
          @click="getContent(Source.LinkInput)"
          v-t:title="'Link'"
        >
          <base-icon name="link" />
        </button>
        <button v-if="contentSourceCount > 3" class="icon-button" @click="openSearchMenu" v-t:title="'Search'">
          <base-icon name="search_web" />
        </button>
        <button
          v-if="contentSourceCount > 4"
          class="icon-button"
          @click="getContentIfSupported(Source.PhotoBooth)"
          v-t:title="'Snap'"
        >
          <label for="content-hidden-image-input"><base-icon name="camera" /></label>
        </button>
        <button v-if="contentSourceCount > 1" class="icon-button" @click="chooseContentSource" v-t:title="'More'">
          <base-icon name="more_horiz" />
        </button>
        <button
          v-if="contentSourceCount <= 1"
          class="icon-button"
          @click="chooseContentSource"
          v-t:title="'Add attachment'"
        >
          <base-icon name="attachment" />
        </button>
      </template>
    </div>
    <surface-beethoven-preview v-if="url" :post="post" :url="url" :width="width" />
    <div
      v-if="showRemoveIcon"
      class="attachment-controls remove-attachment"
      @click.stop="removeAttachment"
      v-t:title="'Remove attachment'"
    >
      <base-icon name="close" />
    </div>
  </div>
</template>

<script>
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { mapGetters, mapState, mapActions } from 'vuex'
import IntlMixin from 'vuemixins/intl_mixin'
import BaseIcon from 'vuecomponents/base_icon'
import SurfaceBeethovenPreview from 'vuecomponents/surface_beethoven_preview'
import UploadFile from 'vuecomponents/upload_file.vue'
import { trackEvent } from 'bits/analytics'
import { $ } from 'bits/jquery'
import { navigateTo, buildUrlFromPath } from 'bits/location'
import fetchNativeBridge from 'bits/native_bridge'
import { isAppUsing } from 'bits/flip'
import { ContentPickerSource } from 'vuexstore/modules/content_picker'

const PHONE_SCREEN_WIDTH = 480
const MAX_STREAM_POST_WIDTH = 540
const MAX_CHAT_POST_WIDTH = 320
const MAX_TABLE_CELL_POST_WIDTH = 300

export default {
  components: { BaseIcon, SurfaceBeethovenPreview, UploadFile },
  mixins: [IntlMixin],
  props: ['post', 'isBeingEdited'],
  data() {
    return {
      width: this.getWidth(),
      Source: ContentPickerSource,
    }
  },
  computed: {
    ...mapState(['isOldApp', 'isMobile']),
    ...mapGetters(['format', 'xTallAttachmentPickerBox', 'uploadLimit']),
    ...mapGetters('browser', ['windowWidth']),
    ...mapGetters('attachment', ['fileUploadingToPickerBox']),
    postWidth() {
      return this.post.width
    },
    url() {
      return this.post.attachment
    },
    showMenu() {
      return this.isBeingEdited && !this.url
    },
    showDeprecatedMenu() {
      return this.isOldApp
    },
    contentSourceCount() {
      return this.width ? Math.floor(this.width / 40) : 5
    },
    showRemoveIcon() {
      return this.isBeingEdited && this.url
    },
    showRegularAttachmentPickerBar() {
      return this.showMenu && !this.showDeprecatedMenu && !this.xTallAttachmentPickerBox
    },
    showTallAttachmentPickerBox() {
      return this.showMenu && !this.showDeprecatedMenu && this.xTallAttachmentPickerBox
    },
  },
  watch: {
    postWidth() {
      this.width = this.getWidth()
    },
    url() {
      this.$nextTick(() => {
        this.width = this.getWidth()
      })
    },
    isBeingEdited() {
      this.width = this.getWidth()
    },
    windowWidth() {
      this.width = this.getWidth()
    },
  },
  mounted() {
    this.width = this.getWidth()
  },
  methods: {
    ...mapActions('contentPicker', ['showContentSourceMenu', 'pickContentSource']),
    ...mapActions('post', ['removePostAttachment', 'changePostAttachment', 'saveContent']),
    ...mapActions('attachment', ['completeUploadingFileToPickerBox']),
    onUploadError() {
      this.completeUploadingFileToPickerBox()
    },
    onUploadCancel() {
      this.completeUploadingFileToPickerBox()
    },
    onUploadDone(file, url) {
      this.saveContent({
        content: {
          link: url,
        },
      })
      this.completeUploadingFileToPickerBox()
    },
    onUploadReject() {
      this.completeUploadingFileToPickerBox()
    },
    getWidth() {
      if (this.isBeingEdited) {
        return Math.ceil($(this.$parent.$el).width() || $('.wish-sizer').width())
      }
      // TODO: get rid of wish-sizer
      const gridSize = Math.ceil($('.wish-sizer').width())
      const MAP_GRID_SIZE = 254
      const TIMELINE_POST_WIDTH = 254
      switch (this.format) {
        case 'stream':
          if (this.windowWidth <= PHONE_SCREEN_WIDTH) {
            return gridSize
          }
          return MAX_STREAM_POST_WIDTH
        case 'grid':
          return Math.ceil(gridSize)
        case 'shelf':
          return gridSize
        case 'matrix':
          return gridSize
        case 'chat':
          if (this.windowWidth <= MAX_CHAT_POST_WIDTH) {
            return gridSize
          }
          return MAX_CHAT_POST_WIDTH
        case 'table':
          return MAX_TABLE_CELL_POST_WIDTH
        case 'map':
          return MAP_GRID_SIZE
        case 'timeline':
          return TIMELINE_POST_WIDTH
        default:
          return Math.ceil(this.post.width) || 1920
      }
    },
    openDeprecatedPicker() {
      const nativeBridge = fetchNativeBridge()
      nativeBridge.postMessage(
        {
          message_type: 'show_content_picker',
          title: this.__('Add attachment'),
          file_type: 'all',
          action: {
            message_type: 'receive_content',
          },
        },
        () => {
          $(document).off('pick:done')
          $(document).on('pick:done', (event, attachment) => {
            this.changePostAttachment({ postCid: this.post.cid, attributes: { attachment } })
            $(document).off('pick:done')
          })
          navigateTo(buildUrlFromPath('/beethoven/attach'))
        },
      )

      nativeBridge.addMessageHandler('receive_content', (msg) => {
        if (msg.url != null) {
          this.changePostAttachment({ postCid: this.post.cid, attributes: { attachment: msg.url } })
        } else {
          console.error('Invalid parameters for receive content.')
        }
      })
    },
    openSearchMenu(e) {
      if (isAppUsing('contentPicker', 1)) {
        var position = {
          left: e.clientX,
          top: e.clientY,
        }
        this.showContentSourceMenu({ position, search: true })
        this.$nextTick(() => {
          document.querySelector('input.search-input').focus()
        })
      } else {
        this.getContent(ContentPickerSource.WebBrowser)
      }
    },
    getContent(source) {
      trackEvent('Content Menu', 'Selected Source', `Wish ${source}`)
      this.pickContentSource({ source })
    },
    getContentIfSupported(source) {
      if (!this.isMobile) {
        this.getContent(source)
      }
    },
    chooseContentSource(e) {
      const position = {
        left: e.clientX,
        top: e.clientY,
      }
      this.showContentSourceMenu({ position: position })
    },
    removeAttachment() {
      this.removePostAttachment({ postCid: this.post.cid })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './../../assets/stylesheets/3/modules/all';

.surface-post-attachment {
  display: block;
  //Hack to get children to honor border radius
  overflow: hidden;

  figure {
    position: relative;
    text-align: center;

    .attachment-preview {
      max-width: 100%;
      //center align hack
      display: table;
      margin-left: auto;
      margin-right: auto;
    }
  }
  &.post-attachment-on-top {
    border-radius: 0 !important;
  }
}

[data-view-mode='edit'] {
  &.surface-post-attachment {
    position: relative;
    @include clearfix;

    .attachment-controls.remove-attachment {
      touch-action: manipulation;
      -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
      position: absolute;
      right: 0;
      top: 0;
      cursor: pointer;
      background-color: #47525d;
      padding: 7px;

      i {
        display: block;
        font-size: 18px;
        color: rgba(white, 0.5);
      }

      &:hover {
        i {
          color: white;
        }
      }
    }
  }
}

.pick-attachment {
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  margin: 4px;
  height: 22px;
  background-image: url('./../../assets/images/attach_something.svg');
  background-position: center center;
  background-size: auto 22px;
  background-repeat: no-repeat;
  opacity: 0.2;

  &:hover {
    opacity: 0.5;
  }
}

.pick-content {
  display: flex;
  align-items: center;
  justify-content: center;

  i {
    color: $caption-text-color;
    body[data-color-scheme='dark'] & {
      color: $inverse-caption-text-color;
    }
  }

  label {
    cursor: pointer;
  }

  &.pick-content-regular {
    .upload-file {
      width: 90%;
    }
  }
}
.pick-content-text {
  position: absolute;
  left: 0;
  right: 0;
  text-align: center;
  font-family: $font-family;
  font-size: 12px;
  margin-top: 24px;
  color: $caption-text-color;
  body[data-color-scheme='dark'] & {
    color: $inverse-caption-text-color;
  }
}
.pick-content-map {
  background: transparent;
  body[data-color-scheme='light'] .surface-post:not([data-color]) & {
    background-color: $grey-1;
  }
  height: 146px;
  background-image: url(../images/mountain-light.png);
  background-size: cover;
  .is-dragover & {
    background-color: rgba($highlight-color, 0.5) !important;
    border: 2px dashed $highlight-color;
    box-sizing: border-box;
  }
  .icon-button {
    @include shadow-z1;
    &:hover {
      @include shadow-z2;
      transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1); // Material ease https://material.io/design/motion/speed.html#easing
    }
    background: #ffffff;
    margin: 0px 2px;
    border-radius: 4px;
  }
  body[data-color-scheme='dark'] & {
    // background: $inverse-grey-1;
    background-image: url(../images/mountain-dark.png);
    background-size: cover;
    .icon-button {
      background: #1a1a1a;
    }
  }
  .icon-button {
    body[data-color-scheme='light'] .surface-post[data-color='red'] & {
      background: #fff0f1;
    }
    body[data-color-scheme='light'] .surface-post[data-color='orange'] & {
      background: #fff9e8;
    }
    body[data-color-scheme='light'] .surface-post[data-color='green'] & {
      background: #eef7ef;
    }
    body[data-color-scheme='light'] .surface-post[data-color='blue'] & {
      background: #e8f7fe;
    }
    body[data-color-scheme='light'] .surface-post[data-color='purple'] & {
      background: #f6ebf8;
    }
    // body[data-color-scheme=light] .surface-post[data-color=pink] & {
    //   background: linear-gradient(0deg, rgba(white, 0.7), rgba(white, 0.7)), var(--post-background-pink);
    // }
    body[data-color-scheme='dark'] .surface-post[data-color='red'] & {
      background: #921616;
    }
    body[data-color-scheme='dark'] .surface-post[data-color='orange'] & {
      background: #cc5900;
    }
    body[data-color-scheme='dark'] .surface-post[data-color='green'] & {
      background: #164b1a;
    }
    body[data-color-scheme='dark'] .surface-post[data-color='blue'] & {
      background: #0a3981;
    }
    body[data-color-scheme='dark'] .surface-post[data-color='purple'] & {
      background: #3b1070;
    }
    // body[data-color-scheme=dark] .surface-post[data-color=pink] & {
    //   background: linear-gradient(0deg, rgba(black, 0.17), rgba(black, 0.17)), var(--post-background-pink);
    // }
  }
}
</style>
